/**
 * Which of the three platform faces the visitor picked in Settings.
 *
 * Kept free of Nuxt composables: the Nitro auth bounce reads the cookie before
 * the app exists. The inline bounce in `app.html` and `public/cs-auth-bounce.js`
 * repeat the cookie name, domains and landing paths; keep all three identical.
 *
 * Opening `/` on a brand domain always switches to that domain's platform.
 * Elsewhere the Settings choice (cookie) wins, then the domain, then Vntru.
 */
export type SiteMode = 'vntru' | 'wazzafni' | 'careerstations'

export const SITE_MODES: SiteMode[] = ['vntru', 'wazzafni', 'careerstations']

export const DEFAULT_SITE_MODE: SiteMode = 'vntru'

export const SITE_MODE_COOKIE = 'cs-site-mode'

export const SITE_MODE_LABELS: Record<SiteMode, string> = {
  vntru: 'Vntru',
  wazzafni: 'Wazzafni',
  careerstations: 'CareerStations',
}

export const SITE_MODE_LANDING: Record<SiteMode, string> = {
  vntru: '/app/shops',
  wazzafni: '/app/cv',
  careerstations: '/app/creator',
}

/** Brand domain → platform. `www.` and the port are ignored. */
export const SITE_MODE_DOMAINS: Record<string, SiteMode> = {
  'vntru.com': 'vntru',
  'wazzafni.com': 'wazzafni',
  'careerstations.com': 'careerstations',
}

/** The platform a brand domain stands for, or `null` on any other host. */
export function siteModeFromHost(host: string): SiteMode | null {
  const hostname = (host.trim().toLowerCase().split(':')[0] ?? '').replace(/^www\./, '')
  return SITE_MODE_DOMAINS[hostname] ?? null
}

export function isSiteMode(value: unknown): value is SiteMode {
  return typeof value === 'string' && (SITE_MODES as string[]).includes(value)
}

export function normalizeSiteMode(value: unknown): SiteMode {
  return isSiteMode(value) ? value : DEFAULT_SITE_MODE
}

/** Route prefixes reachable from each mode's bottom nav. */
const SITE_MODE_PATHS: Record<SiteMode, string[]> = {
  vntru: ['/app/shops', '/app/mall', '/app/cv', '/app/publish', '/app/creator', '/app/profile'],
  wazzafni: ['/app/cv', '/app/publish', '/app/profile'],
  careerstations: ['/app/creator'],
}

export function siteModeLanding(value: unknown): string {
  return SITE_MODE_LANDING[normalizeSiteMode(value)]
}

/**
 * First screen after sign-in or sign-up on Vntru: the Mall tab.
 * An explicit in-app `redirect` still wins. Other platforms return null
 * so their own landing stays in place.
 */
export function vntruAuthLanding(mode: unknown, redirect?: string | null): string | null {
  if (normalizeSiteMode(mode) !== 'vntru') return null
  if (redirect && redirect.startsWith('/app')) return redirect
  return SITE_MODE_LANDING.vntru
}

/**
 * First screen after signup. The domain they signed up on wins over a saved
 * platform choice: vntru.com → shops, wazzafni.com → jobs, careerstations.com
 * → the CareerStations home. Any other host uses the saved platform.
 */
export function siteModeSignupLanding(host: string, fallback?: unknown): string {
  return siteModeLanding(siteModeFromHost(host) ?? fallback)
}

/** Keep `destination` when the mode's nav can reach it, else the mode's landing. */
export function siteModeDestination(value: unknown, destination: string): string {
  const mode = normalizeSiteMode(value)
  const path = destination.split(/[?#]/)[0] ?? ''
  const reachable = SITE_MODE_PATHS[mode].some(prefix => path === prefix || path.startsWith(`${prefix}/`))
  return reachable ? destination : SITE_MODE_LANDING[mode]
}
